import { FC, useState, useEffect, useCallback, memo } from 'react'
import moment from 'moment'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import { DateRangePickerProps } from '../../../@types/common/DatePicker'

const QuarterlyPicker: FC<DateRangePickerProps> = ({ dateRange, onDateRangeChange }) => {
  const [year, setYear] = useState(moment().year())
  const [hoverValue, setHoverValue] = useState<number | null>(null)

  useEffect(() => {
    if (year < dateRange.start.year() || year > dateRange.end.year()) {
      setYear(dateRange.start.year())
    }
  }, [dateRange.start, dateRange.end, year])

  const setStateHandler = useCallback((quarter: number) => {
    if (moment(dateRange.start).year() === moment(dateRange.end).year() && moment(dateRange.start).quarter() === moment(dateRange.end).quarter()) {
      if (moment(dateRange.start).isAfter(moment().year(year).quarter(quarter+1).startOf('quarter'))) {
        // set startDate with the selected quarter
        onDateRangeChange({
          start: moment().year(year).quarter(quarter+1).startOf('quarter'),
          end: dateRange.end.endOf('quarter')
        })
      } else {
        // set endDate with the selected quarter
        onDateRangeChange({
          start: dateRange.start.startOf('quarter'),
          end: moment().year(year).quarter(quarter+1).endOf('quarter')
        })
      }
    } else {
      // set start and end dates with the selected quarter
      onDateRangeChange({
        start: moment().year(year).quarter(quarter+1).startOf('quarter'),
        end: moment().year(year).quarter(quarter+1).endOf('quarter')
      })
    }
  }, [dateRange.start, dateRange.end, year, onDateRangeChange])

  return (
    <div className="text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9">
      <div className="flex items-center text-gray-900">
        <button
          type="button"
          className="flex flex-none items-center justify-center border border-table-text dark:border-revtron-gray2 rounded p-1.5 text-gray-400 hover:text-gray-500"
          onClick={() => setYear(year-1)}
        >
          <span className="sr-only">Previous year</span>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="flex-auto text-base text-black dark:text-white">{year}</div>
        <button
          type="button"
          className="flex flex-none items-center justify-center border border-table-text dark:border-revtron-gray2 rounded p-1.5 text-gray-400 hover:text-gray-500"
          onClick={() => setYear(year+1)}
        >
          <span className="sr-only">Next year</span>
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 text-sm gap-1">
        {Array.from({ length: 4 }, (value, index) => index).map((quarter, index) => {
          const isCurrentQuarter = moment().isSame(moment().year(year).quarter(quarter + 1))
          const isSelectedQuarter = moment(dateRange.start).isSameOrBefore(moment(dateRange.start).year(year).quarter(quarter + 1)) && moment(dateRange.end).isSameOrAfter(moment(dateRange.end).year(year).quarter(quarter + 1))
          let isSelectedQuarterByHover = false
          if (moment(dateRange.start).isSame(moment(dateRange.end).startOf('quarter')) && hoverValue !== null) {
            const hoverDate = moment(dateRange.start).year(year).quarter(hoverValue + 1)
            if (hoverDate.isBefore(dateRange.start)) {
              isSelectedQuarterByHover = moment(hoverDate).isSameOrBefore(moment(dateRange.start).year(year).quarter(quarter+1)) && moment(dateRange.start).isSameOrAfter(moment(dateRange.end).year(year).quarter(quarter+1))
            } else {
              isSelectedQuarterByHover = moment(dateRange.start).isBefore(moment(dateRange.start).year(year).quarter(quarter+1)) && moment(hoverDate).isSameOrAfter(moment(dateRange.end).year(year).quarter(quarter+1))
            }
          }
          return (
            <button
              key={index}
              type="button"
              className={`
                py-2 hover:bg-gray-100 dark:hover:bg-revtron-gray2 focus:z-10 rounded
                ${isCurrentQuarter && 'bg-gray-200 dark:bg-gray-500'}
                ${isSelectedQuarter && 'bg-cyan-200 dark:bg-cyan-500'}
                ${isSelectedQuarterByHover && 'bg-cyan-50 dark:bg-cyan-800'}
              `}
              onClick={() => setStateHandler(quarter)}
              onMouseEnter={() => setHoverValue(quarter)}
              onMouseLeave={() => setHoverValue(null)}
            >
              <div className='mx-auto flex h-5 items-center justify-center rounded-full'>
                {moment().month(quarter*3).format('MMMM')} - {moment().month(quarter*3 + 2).format('MMMM')}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default memo(QuarterlyPicker)
